import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X, Quote } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useSite } from "@/context/SiteContentContext";
import { Reveal, SectionTag } from "./Reveal";
import { fetchVideoTestimonials, toAbsolute } from "@/lib/api";

const DEFAULTS = {
  ar: {
    tag: "شهادات عملائنا",
    title: "قصص نجاح حقيقية",
    subtitle: "استمع إلى عملائنا وهم يحكون كيف ساعدهم فريق مدينتي على تحقيق أهدافهم الرقمية.",
    empty: "قريبًا سنشارك شهادات فيديو من عملائنا الحقيقيين.",
  },
  en: {
    tag: "Client Testimonials",
    title: "Real success stories",
    subtitle: "Hear from our clients how Madinaty helped them achieve their digital goals.",
    empty: "Real video testimonials from our clients coming soon.",
  },
  ku: {
    tag: "ڕەخنەی کڕیارانمان",
    title: "چیرۆکی سەرکەوتنی ڕاستەقینە",
    subtitle: "گوێ بگرە لە کڕیارەکانمان چۆن مەدینەتی یارمەتیدەریان بووە بۆ گەیشتن بە ئامانجە دیجیتاڵییەکان.",
    empty: "بەم زووانە ڕەخنەی ڤیدیۆیی کڕیارانمان بڵاو دەکەینەوە.",
  },
};

// Detect YouTube/Vimeo URL and return embed src
const asEmbed = (url) => {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}?autoplay=1&rel=0`;
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}?autoplay=1`;
  return null;
};

function VideoModal({ item, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);

  const embedSrc = asEmbed(item.video_url);
  const absSrc = toAbsolute(item.video_url);

  return (
    <motion.div
      className="fixed inset-0 z-[80] grid place-items-center bg-black/90 backdrop-blur-md p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      data-testid="video-testimonial-modal"
    >
      <motion.div
        className="relative w-full max-w-4xl aspect-video rounded-2xl overflow-hidden bg-black border border-white/10"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ duration: 0.25 }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 end-3 z-10 h-10 w-10 grid place-items-center rounded-full bg-black/70 border border-white/20 hover:border-[color:var(--primary)] text-white"
          aria-label="Close"
          data-testid="video-modal-close"
        >
          <X size={18} />
        </button>
        {embedSrc ? (
          <iframe
            src={embedSrc}
            className="w-full h-full"
            title={item.client_name}
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <video src={absSrc} className="w-full h-full" controls autoPlay playsInline data-testid="video-modal-player" />
        )}
      </motion.div>
    </motion.div>
  );
}

export default function VideoTestimonials() {
  const { lang } = useLang();
  const { settings } = useSite();
  const [items, setItems] = useState(null);
  const [active, setActive] = useState(null);

  useEffect(() => {
    fetchVideoTestimonials(lang).then((r) => setItems(r.data || [])).catch(() => setItems([]));
  }, [lang]);

  const d = DEFAULTS[lang] || DEFAULTS.ar;
  const tag = settings[`vidtest_tag_${lang}`] || d.tag;
  const title = settings[`vidtest_title_${lang}`] || d.title;
  const subtitle = settings[`vidtest_subtitle_${lang}`] || d.subtitle;

  const list = items || [];
  const isEmpty = items !== null && list.length === 0;

  if (isEmpty) return null; // hide section entirely when no videos exist

  return (
    <section id="video-testimonials" className="relative section-pad overflow-hidden" data-testid="video-testimonials-section">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 30% 20%, rgba(0,242,96,0.08), transparent 55%), radial-gradient(ellipse at 70% 80%, rgba(139,92,246,0.05), transparent 55%)",
        }}
      />
      <div className="relative z-10 mx-auto max-w-6xl px-5 sm:px-8">
        <div className="text-center mb-10 sm:mb-14">
          <Reveal><SectionTag>{tag}</SectionTag></Reveal>
          <Reveal delay={0.05}>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mt-5">{title}</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-slate-400 text-base sm:text-lg mt-4 max-w-2xl mx-auto">{subtitle}</p>
          </Reveal>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6" data-testid="video-testimonials-grid">
          {list.map((v, i) => (
            <Reveal key={v.id || i} delay={0.05 + i * 0.06}>
              <button
                onClick={() => setActive(v)}
                className="group relative block w-full text-start rounded-2xl overflow-hidden card-surface hover:border-[color:var(--primary)]/60 transition-all duration-500 hover:-translate-y-1"
                style={{ boxShadow: "0 10px 40px -12px rgba(0,242,96,0.15)" }}
                data-testid={`video-testimonial-card-${i}`}
              >
                <div className="relative aspect-video overflow-hidden bg-black">
                  {v.poster ? (
                    <img
                      src={toAbsolute(v.poster)}
                      alt={v.client_name}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      onError={(e) => { e.currentTarget.style.opacity = 0.4; }}
                    />
                  ) : (
                    <div
                      className="w-full h-full grid place-items-center"
                      style={{
                        background:
                          "radial-gradient(circle at 50% 50%, rgba(0,242,96,0.2), rgba(0,0,0,0.7))",
                      }}
                    >
                      <Quote size={44} className="text-primary/50" />
                    </div>
                  )}
                  {/* Overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                  {/* Play button */}
                  <div
                    className="absolute inset-0 grid place-items-center"
                    aria-hidden="true"
                  >
                    <div
                      className="h-16 w-16 sm:h-20 sm:w-20 rounded-full grid place-items-center bg-[color:var(--primary)] text-[#04120a] transform group-hover:scale-110 transition-transform duration-500"
                      style={{ boxShadow: "0 0 40px rgba(0,242,96,0.5)" }}
                    >
                      <Play size={28} className="ms-1" fill="currentColor" />
                    </div>
                  </div>
                  {v.duration && (
                    <span className="absolute bottom-2 end-2 rounded-md px-2 py-0.5 text-[11px] font-bold bg-black/70 text-white border border-white/10">
                      {v.duration}
                    </span>
                  )}
                </div>
                <div className="p-5">
                  {v.quote && (
                    <p className="text-slate-300 text-sm leading-relaxed mb-3 line-clamp-3 italic">
                      &quot;{v.quote}&quot;
                    </p>
                  )}
                  <div className="flex items-center gap-3">
                    <div
                      className="h-10 w-10 rounded-full grid place-items-center font-bold text-[#04120a] shrink-0"
                      style={{ background: "var(--primary)" }}
                    >
                      {(v.client_name || "?").trim().charAt(0)}
                    </div>
                    <div className="min-w-0">
                      <div className="font-extrabold truncate">{v.client_name}</div>
                      <div className="text-xs text-slate-400 truncate">
                        {[v.client_role, v.company].filter(Boolean).join(" — ")}
                      </div>
                    </div>
                  </div>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && <VideoModal item={active} onClose={() => setActive(null)} />}
      </AnimatePresence>
    </section>
  );
}
